import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { confirmDialog } from "../../../dialogs";
import { pickAlbum } from "../../../albumPicker";
import { showToast } from "../../../toast";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import {
  loadGiftsData,
  saveGiftToAlbumApi,
  applyGiftUnlocked,
  applyGiftReceived,
  applyGiftReaction,
  applyGiftCancelled,
  applyGiftEdited,
} from "../../../giftsApi";
import { setGiftSocketHandlers, onOpenTogetherStart } from "../../../chatSocket";
import { GiftViewerDialog } from "./GiftViewerDialog";
import { GiftCard } from "./GiftCard";
import { PrivateDropCard } from "./PrivateDropCard";
import type { Gift } from "../../../types";

type GiftFilter = "all" | "sent" | "received";

function GiftsSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        <div className="gift-card-skeleton skeleton-card" key={i} />
      ))}
    </>
  );
}

function GiftsEmptyState({ filter, onSend }: { filter: GiftFilter; onSend: () => void }) {
  const copy =
    filter === "received"
      ? { title: "Nothing received yet.", subtitle: "", showSend: false }
      : filter === "sent"
        ? { title: "You haven't sent a gift yet.", subtitle: "", showSend: true }
        : { title: "No gifts yet", subtitle: "Send something private and unexpected.", showSend: true };

  return (
    <div className="gifts-empty-state">
      <i className="bi bi-gift text-pink" aria-hidden="true" />
      <div className="gifts-empty-title">{copy.title}</div>
      {copy.subtitle && <p className="gifts-empty-subtitle">{copy.subtitle}</p>}
      {copy.showSend && (
        <button type="button" className="btn btn-pink btn-sm" onClick={onSend}>
          <i className="bi bi-plus-lg me-1" aria-hidden="true" />
          Send a Gift
        </button>
      )}
    </div>
  );
}

function GiftsBody() {
  const navigate = useNavigate();
  const allGifts = useAppState((s) => s.gifts);
  const [filter, setFilter] = useState<GiftFilter>("all");
  const [loaded, setLoaded] = useState(false);
  // A viewed "once" gift is permanently deleted server-side (GiftsDB.markViewed)
  // but its tombstone record (message, who/when, viewedAt) survives - this is
  // the gift-history view that record was always meant to feed, so a viewed
  // "once" gift now stays visible instead of disappearing from the list.
  const gifts = allGifts.filter((g) => filter === "all" || g.direction === filter);
  const [viewingGift, setViewingGift] = useState<Gift | null>(null);

  useEffect(() => {
    loadGiftsData().finally(() => setLoaded(true));
  }, []);

  // Registered here, not gated behind the container-found check below - this
  // component is unconditionally mounted in AppRoot (like ChatScreen), so
  // the effect must run regardless of whether the Gifts tab is currently
  // open, or an unlock push while the user is elsewhere would go unhandled.
  useEffect(() => {
    setGiftSocketHandlers({
      onGiftReceived: (gift) => {
        applyGiftReceived(gift);
        showToast(`${gift.senderName} sent you a gift!`, "success");
      },
      onGiftUnlocked: (gift) => {
        applyGiftUnlocked(gift);
        if (gift.deliveryMode === "unlock_later" && navigator.vibrate) navigator.vibrate(10);
        showToast(
          gift.deliveryMode === "unlock_later"
            ? `Your Private Drop from ${gift.senderName} is ready!`
            : `A gift from ${gift.senderName} just unlocked!`,
          "success",
        );
      },
      onGiftReaction: (data) => {
        const gift = appState.gifts.find((g) => g.id === data.giftId);
        applyGiftReaction(data);
        if (gift?.deliveryMode === "unlock_later") {
          const emoji = data.reactions.find((r) => r.userId === gift.recipientId)?.emoji;
          if (emoji) showToast(`${gift.recipientName} reacted ${emoji}`, "success");
        }
      },
      onGiftCancelled: applyGiftCancelled,
      onGiftEdited: applyGiftEdited,
    });
  }, []);

  // Private Drops V2 - Open Together's synchronized reveal. Fired
  // regardless of which screen the user is currently on (see
  // openingGiftId's own comment in state.ts for why this indirection is
  // needed); this component is unconditionally mounted, so it's always
  // listening.
  useEffect(() => {
    return onOpenTogetherStart((data) => {
      if (navigator.vibrate) navigator.vibrate(25);
      // The sender can never fetch their own sent gift's media (server-
      // enforced) - "together" here means synchronized TIMING, not that
      // both sides look at the same reveal dialog. Only the recipient's
      // client actually opens it; the sender gets a lighter confirmation.
      const gift = appState.gifts.find((g) => g.id === data.giftId);
      if (gift?.direction === "sent") {
        showToast("Reveal started - they're opening it now", "success");
        return;
      }
      appState.openingGiftId = data.giftId;
      notifyStateChange();
    });
  }, []);

  const openingGiftId = useAppState((s) => s.openingGiftId);
  useEffect(() => {
    if (!openingGiftId) return;
    const gift = allGifts.find((g) => g.id === openingGiftId);
    if (gift) setViewingGift(gift);
    appState.openingGiftId = null;
    notifyStateChange();
  }, [openingGiftId, allGifts]);

  const container = getVanillaEl<HTMLDivElement>("giftsView");
  if (!container) return null;

  async function handleView(gift: Gift): Promise<void> {
    // Every gift card is tappable now (spec: whole-card tap, not a
    // conditionally-rendered button) - this is the same canView gate the
    // old "View" button's own conditional render used to provide, moved
    // here since GiftCard.tsx just forwards every tap unconditionally. A
    // virtual gift has no attachment to check (there's no file - the
    // "content" is the always-present catalog emoji/name) and is always
    // mode "keep" (server-enforced), so it's viewable any time it's
    // received and unlocked.
    const canView =
      gift.direction === "received" &&
      !gift.locked &&
      (gift.type === "virtual" || (gift.mode === "once" ? !gift.viewedAt : !!gift.attachment));
    if (!canView) return;

    if (gift.mode === "once") {
      const ok = await confirmDialog({
        title: "View Once",
        message: "This gift can only be viewed once - it will be permanently deleted right after. View it now?",
        confirmLabel: "View",
      });
      if (!ok) return;
    }
    // "keep" gifts aren't destructive to open - GiftViewerDialog's close
    // handler calls markGiftViewedApi() regardless of mode, but the server
    // only ever deletes anything for "once" gifts (see GiftsDB.markViewed).
    setViewingGift(gift);
  }

  async function handleSaveToAlbum(gift: Gift): Promise<void> {
    const chosen = await pickAlbum("Save to Album", { includeAll: false });
    if (!chosen) return;
    const result = await saveGiftToAlbumApi(gift.id, chosen.id);
    if (!result.ok) {
      showToast(result.error || "Failed to save gift.", "error");
      return;
    }
    showToast(`Saved to "${chosen.name}"`, "success");
  }

  return createPortal(
    <>
      <div className="gifts-header">
        <button type="button" className="gifts-header-back" aria-label="Back" onClick={() => navigate("/")}>
          <i className="bi bi-arrow-left" aria-hidden="true" />
        </button>
        <div className="gifts-header-titles">
          <h1 className="gifts-header-title">Gifts</h1>
          <p className="gifts-header-subtitle">Thoughtful surprises, just between you two.</p>
        </div>
        <button type="button" className="gifts-send-btn" onClick={() => navigate("/gifts/send")}>
          <i className="bi bi-plus-lg" aria-hidden="true" />
          Send a Gift
        </button>
      </div>

      <div className="gifts-filter" role="group" aria-label="Filter gifts">
        {(["all", "received", "sent"] as const).map((f) => (
          <button
            key={f}
            type="button"
            className={filter === f ? "is-active" : ""}
            aria-pressed={filter === f}
            aria-label={f === "all" ? "All gifts" : f === "received" ? "Received gifts" : "Sent gifts"}
            onClick={() => setFilter(f)}
          >
            {f === "all" ? "All" : f === "received" ? "Received" : "Sent"}
          </button>
        ))}
      </div>

      {loaded && gifts.length > 0 && <div className="gifts-section-label">Recent Gifts</div>}

      <div className="gifts-list">
        {!loaded ? (
          <GiftsSkeleton />
        ) : !gifts.length ? (
          <GiftsEmptyState filter={filter} onSend={() => navigate("/gifts/send")} />
        ) : (
          gifts.map((g) =>
            g.deliveryMode === "unlock_later" && !g.viewedAt ? (
              <PrivateDropCard key={g.id} gift={g} onOpen={handleView} />
            ) : (
              <GiftCard key={g.id} gift={g} onView={handleView} onSaveToAlbum={handleSaveToAlbum} />
            ),
          )
        )}
      </div>

      {viewingGift && <GiftViewerDialog gift={viewingGift} onClose={() => setViewingGift(null)} />}
    </>,
    container,
  );
}

export function GiftsScreen() {
  return <GiftsBody />;
}
